import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import Icon from "@mui/material/Icon";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSnackbar from "components/SoftSnackbar";
import SoftSelect from "components/SoftSelect";

import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";

const DEVICE_META = {
  desktop: {
    label: "Desktop",
    short: "D",
    color: "#3b82f6",
    emptyColor: "#dbeafe",
  },
  mobile: {
    label: "Mobile",
    short: "M",
    color: "#10b981",
    emptyColor: "#d1fae5",
  },
  tablet: {
    label: "Tablet",
    short: "T",
    color: "#f59e0b",
    emptyColor: "#fef3c7",
  },
};

const DEVICE_ORDER = ["desktop", "mobile", "tablet"];
const ALERT_COLOR = "#e53935";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: "42px !important",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-input": {
    height: "42px !important",
    padding: "0 12px !important",
    display: "flex",
    alignItems: "center",
  },
};

const filterLabelSx = {
  display: "block",
  mb: 0.7,
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: 1.35,
};

const filterSelectSx = {
  "& .select__control": {
    minHeight: "42px !important",
  },
};

const asArray = (payload) => (Array.isArray(payload) ? payload : []);

const formatTime = (value) => {
  if (!value) return "N/A";
  return String(value).slice(0, 5);
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const deviceNeedsAlert = (device, total) => {
  const count = Number(device?.total || 0);
  if (!count || !total) return false;

  const deviceRatio = (count / total) * 100;

  return deviceRatio >= 70;
};

const aggregateMetricBrowsers = (devices) => {
  const browserTotals = {};

  DEVICE_ORDER.forEach((key) => {
    Object.entries(devices?.[key]?.browsers || {}).forEach(([name, count]) => {
      const browserName = name || "Unknown";
      browserTotals[browserName] = (browserTotals[browserName] || 0) + Number(count || 0);
    });
  });

  return Object.entries(browserTotals)
    .map(([name, count]) => ({ name, count: Number(count || 0) }))
    .sort((a, b) => b.count - a.count);
};

function MetricTooltip({ metric, metricLabel }) {
  const total = Number(metric?.total || 0);
  const browsers = aggregateMetricBrowsers(metric?.devices || {});

  return (
    <Box
      sx={{
        p: 1.2,
        bgcolor: "#111827",
        color: "#fff",
        borderRadius: "8px",
        minWidth: 220,
        maxWidth: 320,
        boxShadow: "0 10px 24px rgba(0,0,0,0.28)",
      }}
    >
      <SoftTypography sx={{ color: "#fff", fontSize: "13px", fontWeight: 400 }}>
        Total {metricLabel}: {total}
      </SoftTypography>

      <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(255,255,255,0.14)" }}>
        {browsers.length > 0 ? (
          browsers.map((browser) => {
            const ratio = total > 0 ? ((browser.count / total) * 100).toFixed(1) : "0.0";
            const alert = Number(ratio) >= 70;

            return (
              <Box
                key={browser.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 0.6,
                }}
              >
                <SoftTypography
                  sx={{
                    color: alert ? "#ff6b6b" : "#fff",
                    fontSize: "12px",
                    fontWeight: 400,
                    maxWidth: 160,
                    whiteSpace: "normal",
                  }}
                >
                  {browser.name || "Unknown"}
                </SoftTypography>
                <SoftTypography
                  sx={{
                    color: alert ? "#ff6b6b" : "#fff",
                    fontSize: "12px",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {browser.count} ({ratio}%)
                </SoftTypography>
              </Box>
            );
          })
        ) : (
          <SoftTypography sx={{ color: "#d1d5db", fontSize: "12px" }}>
            No browser data
          </SoftTypography>
        )}
      </Box>
    </Box>
  );
}

MetricTooltip.propTypes = {
  metric: PropTypes.shape({
    total: PropTypes.number,
    devices: PropTypes.objectOf(
      PropTypes.shape({
        total: PropTypes.number,
        browsers: PropTypes.objectOf(PropTypes.number),
      })
    ),
  }).isRequired,
  metricLabel: PropTypes.string.isRequired,
};

function DeviceMetricCell({ metric, metricLabel }) {
  const total = Number(metric?.total || 0);
  const devices = metric?.devices || {};
  const preparedDevices = DEVICE_ORDER.map((key) => {
    const meta = DEVICE_META[key];
    const device = {
      ...meta,
      ...(devices[key] || {}),
      total: Number(devices[key]?.total || 0),
      browsers: devices[key]?.browsers || {},
    };

    return {
      key,
      meta,
      device,
      alert: deviceNeedsAlert(device, total),
    };
  });

  return (
    <Box sx={{ minWidth: 188, width: "100%" }}>
      <SoftTypography
        variant="caption"
        sx={{ display: "block", color: "#475569", fontSize: "12px", mb: 0.5 }}
      >
        Total: {total}
      </SoftTypography>

      <Tooltip
        title={<MetricTooltip metric={metric} metricLabel={metricLabel} />}
        arrow
        TransitionComponent={Zoom}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "transparent !important",
              p: 0,
              maxWidth: "none",
              boxShadow: "none",
            },
          },
          arrow: {
            sx: {
              color: "#111827 !important",
            },
          },
        }}
      >
        <Box
          sx={{
            height: 30,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            overflow: "hidden",
            borderRadius: "6px",
            border: "1px solid #d8dee9",
            bgcolor: "#f8fafc",
            cursor: "pointer",
          }}
        >
          {preparedDevices.map(({ key, meta, device, alert }, index) => {
            const background = device.total === 0 ? meta.emptyColor : alert ? ALERT_COLOR : meta.color;
            const textColor = device.total === 0 ? "#334155" : "#fff";

            return (
              <Box
                key={key}
                sx={{
                  bgcolor: background,
                  color: textColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: index < preparedDevices.length - 1 ? "1px solid rgba(15,23,42,0.14)" : "none",
                  transition: "filter 0.18s ease",
                  "&:hover": {
                    filter: "brightness(0.95)",
                  },
                }}
              >
                <SoftTypography
                  sx={{
                    color: textColor,
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {meta.short}: {device.total}
                </SoftTypography>
              </Box>
            );
          })}
        </Box>
      </Tooltip>
    </Box>
  );
}

DeviceMetricCell.propTypes = {
  metric: PropTypes.shape({
    total: PropTypes.number,
    devices: PropTypes.objectOf(
      PropTypes.shape({
        total: PropTypes.number,
        browsers: PropTypes.objectOf(PropTypes.number),
      })
    ),
  }).isRequired,
  metricLabel: PropTypes.string.isRequired,
};

function UserHits() {
  const token = localStorage.getItem("authToken");

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [subBranches, setSubBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSubBranch, setSelectedSubBranch] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [show, setShow] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [fromData, setFromData] = useState(0);
  const [toData, setToData] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const toggleSnackbar = () => setShow(!show);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API_URL}/branches`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      const payload = asArray(data?.data);

      setBranches(
        payload.map((branch) => ({
          value: branch.id,
          label: branch.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchSubBranches = async (branchId) => {
    try {
      const response = await fetch(`${API_URL}/sub-branches/by-branch/${branchId}`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      const payload = asArray(data?.data);

      setSubBranches(
        payload.map((subBranch) => ({
          value: subBranch.id,
          label: subBranch.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching sub branches:", error);
      setSubBranches([]);
    }
  };

  const fetchShifts = async (subBranchId) => {
    try {
      const response = await fetch(`${API_URL}/sub-branches/${subBranchId}/shifts`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      const payload = asArray(data?.shifts);

      setShifts(
        payload.map((shift) => ({
          value: shift.id,
          label:
            shift.label ||
            `${shift.type || "Shift"} (${shift.shift_code || "-"} ${formatTime(
              shift.start_time
            )}-${formatTime(shift.end_time)})`,
        }))
      );
    } catch (error) {
      console.error("Error fetching shifts:", error);
      setShifts([]);
    }
  };

  const fetchUsersByShift = async (shiftId) => {
    try {
      const response = await fetch(`${API_URL}/shifts/${shiftId}/users`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      const payload = asArray(data?.users ?? data?.data);

      setUsers(
        payload.map((user) => ({
          value: user.id,
          label: user.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchUserHits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("per_page", perPage);

      if (searchQuery.trim()) params.append("q", searchQuery.trim());
      if (selectedBranch?.value) params.append("branch_id", selectedBranch.value);
      if (selectedSubBranch?.value) params.append("subbranch_id", selectedSubBranch.value);
      if (selectedShift?.value) params.append("shift_id", selectedShift.value);
      if (fromDate) params.append("start_date", fromDate);
      if (toDate) params.append("end_date", toDate);
      if (selectedUser?.value) params.append("user_id", selectedUser.value);

      const response = await fetch(`${API_URL}/studies/user-hits?${params.toString()}`, {
        method: "GET",
        headers: authHeaders,
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data?.message || "Failed to load user hits");
      }

      setRows(asArray(data.data));
      setTotalRecords(Number(data.pagination?.total || 0));
      setTotalPages(Number(data.pagination?.last_page || 1));
      setFromData(Number(data.pagination?.from || 0));
      setToData(Number(data.pagination?.to || 0));
    } catch (error) {
      console.error("Error fetching user hits:", error);
      setRows([]);
      setMessage(error.message || "Failed to load user hits");
      setShow(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchUserHits();
  }, [
    currentPage,
    perPage,
    searchQuery,
    selectedBranch,
    selectedSubBranch,
    selectedShift,
    selectedUser,
    fromDate,
    toDate,
  ]);

  const handleBranchChange = (option) => {
    setCurrentPage(1);
    setSelectedBranch(option);
    setSelectedSubBranch(null);
    setSelectedShift(null);
    setSelectedUser(null);
    setFromDate("");
    setToDate("");
    setSubBranches([]);
    setShifts([]);
    setUsers([]);

    if (option?.value) fetchSubBranches(option.value);
  };

  const handleSubBranchChange = (option) => {
    setCurrentPage(1);
    setSelectedSubBranch(option);
    setSelectedShift(null);
    setSelectedUser(null);
    setFromDate("");
    setToDate("");
    setShifts([]);
    setUsers([]);

    if (option?.value) fetchShifts(option.value);
  };

  const handleShiftChange = (option) => {
    setCurrentPage(1);
    setSelectedShift(option);
    setSelectedUser(null);
    setFromDate("");
    setToDate("");
    setUsers([]);

    if (option?.value) fetchUsersByShift(option.value);
  };

  const handleUserChange = (option) => {
    setCurrentPage(1);
    setSelectedUser(option || null);

    if (!option) {
      setFromDate("");
      setToDate("");
    }
  };

  const showSelectUserFirstMessage = () => {
    setMessage("Please select user first");
    setShow(true);
  };

  const handleDateFilterChange = (setter) => (event) => {
    const value = event.target.value;

    if (value && !selectedUser?.value) {
      showSelectUserFirstMessage();
      return;
    }

    setCurrentPage(1);
    setter(value);
  };

  const handleDateFilterFocus = () => {
    if (!selectedUser?.value) {
      showSelectUserFirstMessage();
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    if (value === "") {
      setCurrentPage("");
      return;
    }
    const pageNumber = Number(value);
    if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((page) => page + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((page) => page - 1);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color="primary"
        icon="notifications"
        title="User Hits"
        content={message ?? ""}
        open={show}
        close={toggleSnackbar}
        onClose={() => setShow(false)}
      />

      <Card sx={{ mb: 2, px: 2, py: 2, width: "100%", borderRadius: "8px", overflow: "visible" }}>
        <div className="row g-3 align-items-start">
          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Branch
            </SoftTypography>
            <SoftSelect
              classNamePrefix="select"
              name="branch"
              options={branches}
              isMulti={false}
              size="medium"
              placeholder="Select..."
              onChange={handleBranchChange}
              value={selectedBranch}
              isClearable
              sx={filterSelectSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Sub Branch
            </SoftTypography>
            <SoftSelect
              classNamePrefix="select"
              name="sub_branch"
              options={subBranches}
              isMulti={false}
              size="medium"
              placeholder="Select..."
              onChange={handleSubBranchChange}
              value={selectedSubBranch}
              isDisabled={!selectedBranch}
              isClearable
              sx={filterSelectSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Shift
            </SoftTypography>
            <SoftSelect
              classNamePrefix="select"
              name="shift"
              options={shifts}
              isMulti={false}
              size="medium"
              placeholder="Select..."
              onChange={handleShiftChange}
              value={selectedShift}
              isDisabled={!selectedSubBranch}
              isClearable
              sx={filterSelectSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Users
            </SoftTypography>
            <SoftSelect
              classNamePrefix="select"
              name="user_id"
              options={users}
              isMulti={false}
              size="medium"
              placeholder="Select..."
              onChange={handleUserChange}
              value={selectedUser}
              isDisabled={!selectedShift}
              isClearable
              sx={filterSelectSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Start Date
            </SoftTypography>
            <SoftInput
              type="datetime-local"
              fullWidth
              value={fromDate}
              onFocus={handleDateFilterFocus}
              onChange={handleDateFilterChange(setFromDate)}
              sx={inputSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              End Date
            </SoftTypography>
            <SoftInput
              type="datetime-local"
              fullWidth
              value={toDate}
              onFocus={handleDateFilterFocus}
              onChange={handleDateFilterChange(setToDate)}
              sx={inputSx}
            />
          </div>

          <div className="col-12 col-md-3 col-xl">
            <SoftTypography variant="caption" sx={filterLabelSx}>
              Search
            </SoftTypography>
            <SoftInput
              fullWidth
              value={searchQuery}
              onChange={(event) => {
                setCurrentPage(1);
                setSearchQuery(event.target.value);
              }}
              placeholder="Search user..."
              sx={inputSx}
            />
          </div>
        </div>
      </Card>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
          <span style={{ color: "rgb(23, 193, 232)", fontWeight: 400 }}>Showing</span>{" "}
          {fromData} to {toData} of {totalRecords.toLocaleString()}{" "}
          <span style={{ color: "rgb(23, 193, 232)", fontWeight: 400 }}>entries</span>
        </SoftTypography>

        <Box display="flex" alignItems="center" gap={0.5}>
          <SoftPagination item onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            <Icon sx={{ fontSize: 16 }}>first_page</Icon>
          </SoftPagination>
          <SoftPagination item onClick={handlePreviousPage} disabled={currentPage === 1}>
            <Icon sx={{ fontSize: 16 }}>chevron_left</Icon>
          </SoftPagination>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mx: 1, flexWrap: "nowrap" }}>
            <SoftInput
              inputProps={{
                type: "number",
                min: 1,
                max: totalPages,
                style: { textAlign: "center", fontSize: "14px", padding: "4px 8px", lineHeight: "32px" },
              }}
              value={currentPage}
              onChange={handleInputChange}
              sx={{ width: "54px", "& .MuiOutlinedInput-root": { height: "32px" } }}
            />
            <SoftTypography
              component="span"
              variant="body2"
              color="textSecondary"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                minWidth: 28,
                height: 32,
                lineHeight: "32px",
                whiteSpace: "nowrap",
                fontSize: "14px",
              }}
            >
              / {totalPages}
            </SoftTypography>
          </Box>
          <SoftPagination item onClick={handleNextPage} disabled={currentPage === totalPages}>
            <Icon sx={{ fontSize: 16 }}>chevron_right</Icon>
          </SoftPagination>
          <SoftPagination item onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            <Icon sx={{ fontSize: 16 }}>last_page</Icon>
          </SoftPagination>
        </Box>
      </Box>

      <Card sx={{ overflow: "hidden", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
        <SoftBox p={2}>
          <SoftTypography variant="h6" sx={{ color: "#333", mb: 2, fontSize: "16px", fontWeight: 400 }}>
            User Hits
          </SoftTypography>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" alignItems="center" height="400px">
              <FlagLoader />
            </SoftBox>
          ) : rows.length > 0 ? (
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table sx={{ width: "100%", minWidth: { xs: 920, lg: "100%" }, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {[
                      "Branch",
                      "Sub Branch",
                      "User",
                      "Date",
                      "Hits",
                      "Completes",
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{
                          color: "#475569",
                          fontWeight: 400,
                          fontSize: "11px",
                          textTransform: "uppercase",
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                          px: 0.75,
                          py: 1.2,
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={`${row.user?.id}-${row.shift?.id || "no-shift"}-${row.date}-${row.window?.start || ""}`}
                      hover
                    >
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <SoftTypography sx={{ fontSize: "13px" }}>
                          {row.branch?.name || "N/A"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <SoftTypography sx={{ fontSize: "13px" }}>
                          {row.subbranch?.name || "N/A"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <SoftTypography sx={{ fontSize: "13px", color: "#0f172a" }}>
                          {row.user?.name || "N/A"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <SoftTypography sx={{ fontSize: "13px" }}>
                          {row.date || "N/A"}
                        </SoftTypography>
                        <SoftTypography
                          variant="caption"
                          sx={{ display: "block", color: "#64748b" }}
                        >
                          {formatDateTime(row.window?.start)} - {formatDateTime(row.window?.end)}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <DeviceMetricCell metric={row.hits} metricLabel="Hits" />
                      </TableCell>
                      <TableCell sx={{ px: 0.75, py: 1.2 }}>
                        <DeviceMetricCell metric={row.completes} metricLabel="Completes" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
              <Icon sx={{ fontSize: 50, color: "#cbd5e1", mb: 1 }}>bar_chart</Icon>
              <SoftTypography variant="body2" color="textSecondary" gutterBottom fontSize="14px">
                No data found
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>

      <Footer />
    </DashboardLayout>
  );
}

export default UserHits;
